import { parseLocalDateOnly } from "@/lib/dateFormat";
import { flattenAgenda } from "@/pages/dashboard/dashboardAlertsModel";
import type { FiscalAgendaObligation } from "@/services/fiscalService";

/**
 * Selection + relative-date logic for the dashboard's "próximo vencimiento"
 * card. Kept out of the component so it can be tested directly — the rules are
 * ordering- and timezone-sensitive and the card is the only place a user finds
 * out an obligation is overdue.
 */

/**
 * The obligation the user most needs to see, matching the mobile Inicio
 * screen's rule: any overdue one wins — the most recent, since that's the most
 * actionable — otherwise the soonest upcoming.
 *
 * Note this deliberately does NOT reuse useUpcomingObligationsTimeline: that
 * hook filters to `dueDate >= today`, so an overdue obligation never reaches
 * its output and this card would report "sin vencimientos próximos" to exactly
 * the user who has one outstanding.
 */
export function pickNextObligation(
  grouped: Record<string, FiscalAgendaObligation[]> | undefined,
): FiscalAgendaObligation | null {
  if (!grouped) return null;
  // flattenAgenda already sorts ascending by dueDate.
  const pending = flattenAgenda(grouped).filter((o) => o.status !== "completed");
  if (pending.length === 0) return null;

  const today = startOfToday();
  const overdue = pending.filter((o) => parseLocalDateOnly(o.dueDate) < today);
  if (overdue.length > 0) return overdue[overdue.length - 1] ?? null;
  return pending[0] ?? null;
}

export interface ObligationTiming {
  /** Whole days from today. Negative when the due date has passed. */
  days: number;
  isOverdue: boolean;
}

export function describeObligation(
  obligation: FiscalAgendaObligation,
): ObligationTiming {
  const today = startOfToday();
  const due = parseLocalDateOnly(obligation.dueDate);
  // Rounded rather than floored: across a DST transition the span between two
  // local midnights isn't an exact multiple of 24h, and truncation would
  // report a day less.
  const days = Math.round(
    (due.getTime() - today.getTime()) / (24 * 60 * 60 * 1000),
  );
  return { days, isOverdue: days < 0 };
}

/**
 * Local midnight. Both sides of every comparison here go through
 * parseLocalDateOnly, which builds a local-midnight Date from the calendar
 * parts — comparing against a raw `new Date()` would make an obligation due
 * today read as overdue for the rest of the day.
 */
function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}
